import { useQuery } from "@apollo/client";
import { uniqBy } from "lodash";
import { useMemo, useState } from "react";
import {
  GetProfileCareerQueryQuery,
  GetProfileCareerQueryQueryVariables,
} from "types/graphql-schema";

import { personCareerPath } from "@apps/org-settings/components/competencies/helpers";
import PersonRoleEmptyState from "@apps/org-settings/components/competencies/person-role-empty-state";
import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { Select, SelectOption } from "@components/select/select";
import useUrlQueryParams from "@helpers/hooks/use-url-query-params";

import ProfileReviews from "../overview-tab/components/profile-reviews";
import ProfileCareerTable from "./components/career-table";
import getProfileCareerQuery from "./graphql/get-profile-career-query";

const ProfileCareerTab = ({ profileId }: { profileId: number }) => {
  const label = useLabel();
  const queryParams = useUrlQueryParams<{
    careerTrackId: number | null;
  }>({
    careerTrackId: null,
  });
  const currentOrganization = currentOrganizationVar();
  const { data, loading, refetch } = useQuery<
    GetProfileCareerQueryQuery,
    GetProfileCareerQueryQueryVariables
  >(getProfileCareerQuery, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    variables: { userId: profileId, organizationId: currentOrganization.id },
  });

  const profile = data?.user;
  const roles = useMemo(() => profile?.currentRoles || [], [profile]);
  const tracks = useMemo(
    () =>
      uniqBy(
        roles.map((role) => role.careerTrack),
        "id",
      ).filter(Boolean),
    [roles],
  );
  const userRolesIds = useMemo(() => roles.map((role) => role.id), [roles]);
  const careerTrackOptions = useMemo(() => {
    return tracks.length > 0
      ? tracks.map((track) => ({
          label: track.title,
          value: track.id,
        }))
      : [{ label: "Select a career track...", value: 0 }];
  }, [tracks]);

  const suggestedTrackId = careerTrackOptions[0]?.value;
  const [_selectedCareerTrackId, setSelectedCareerTrackId] = useState<
    number | null
  >(queryParams.careerTrackId ? Number(queryParams.careerTrackId) : null);
  const selectedCareerTrackId = tracks.some(
    (track) => track.id === _selectedCareerTrackId,
  )
    ? _selectedCareerTrackId
    : suggestedTrackId;
  const selectedTrack = tracks.find(
    (track) => track.id === selectedCareerTrackId,
  );

  const handleCareerTrackChange = (option: SelectOption<number>) => {
    setSelectedCareerTrackId(option.value);
  };

  const isAdmin = isAdminVar();
  // Admins, and the person's manager, edit their responsibilities on the
  // person's Career Tracks page in settings.
  const canEditCareer = !!data?.canEditUserCareerCriteria;

  return (
    <div className="flex flex-col gap-12">
      <ProfileReviews profileId={profileId} bare />
      {loading ? (
        <Loading />
      ) : !profile ? (
        <div className="text-gray-500 text-sm text-center">
          No profile found.
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pt-2">
            <div className="text-lg font-semibold text-gray-900 whitespace-nowrap">
              Career Tracks
            </div>
            {tracks.length > 0 && (
              <div className="flex flex-wrap gap-x-4 gap-y-2 items-center text-gray-500">
                My Career Track(s)
                <Select<number>
                  options={careerTrackOptions}
                  value={selectedCareerTrackId ?? 0}
                  onChange={handleCareerTrackChange}
                  buttonProps={{ size: ButtonSize.medium }}
                />
                {/* This person's view in settings, where an admin or their
                    manager can change what's expected of them. */}
                {canEditCareer && (
                  <Button
                    size={ButtonSize.medium}
                    text={`Open in ${label("career", { capitalize: true })} Tracks`}
                    aria-label="Profile career: Open in career tracks"
                    to={personCareerPath(currentOrganization.id, profile.id)}
                  />
                )}
              </div>
            )}
          </div>
          {tracks.length === 0 ? (
            <div className="mt-6">
              {/* The same as on their Career Tracks page: assign a role,
                  or start a track when there's none to assign. */}
              <PersonRoleEmptyState
                organizationId={currentOrganization.id}
                person={profile}
                canManageTracks={isAdmin}
                isSelf={profile.id === currentUserVar().id}
                onRoleAssigned={() => refetch()}
              />
            </div>
          ) : selectedTrack ? (
            <ProfileCareerTable
              key={selectedCareerTrackId}
              profile={profile}
              userRolesIds={userRolesIds}
              selectedCareerTrack={selectedTrack}
            />
          ) : (
            <div className="text-gray-500 text-sm text-center">
              No career track selected.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileCareerTab;
